// Cart-specific product recommendations that extend the existing system
class CartProductRecommendations extends HTMLElement {
  observer = undefined;
  swiperInitialized = false; // Add flag to prevent multiple initializations

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  async loadRecommendations(productId) {
    try {
      const url = this.dataset.url;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Filter recommendations to exclude cart products and get only product cards
      const filteredCards = this.filterRecommendations(html);
      
      if (filteredCards && filteredCards.length > 0) {
        this.createSliderWithProducts(filteredCards);
      } else {
        this.showNoRecommendations();
      }
      
    } catch (error) {
      console.error('Error loading recommendations:', error);
      this.showNoRecommendations();
    }
  }

  extractProductCardsFromHTML(html) {
    try {
      // Sanitize HTML to prevent SVG parsing errors
      const sanitizeHTML = (html) => {
        return html.replace(/<svg[^>]*>.*?<\/svg>/gs, '')
                   .replace(/<path[^>]*>/g, '')
                   .replace(/<g[^>]*>.*?<\/g>/gs, '')
                   .replace(/<defs[^>]*>.*?<\/defs>/gs, '')
                   .replace(/<clipPath[^>]*>.*?<\/clipPath>/gs, '')
                   .replace(/<use[^>]*>/g, '')
                   .replace(/<symbol[^>]*>.*?<\/symbol>/gs, '')
                   .replace(/<filter[^>]*>.*?<\/filter>/gs, '')
                   .replace(/<feGaussianBlur[^>]*>/g, '')
                   .replace(/<feOffset[^>]*>/g, '')
                   .replace(/<feComposite[^>]*>/g, '')
                   .replace(/<feBlend[^>]*>/g, '')
                   .replace(/<feColorMatrix[^>]*>/g, '')
                   .replace(/<feMerge[^>]*>.*?<\/feMerge>/gs, '')
                   .replace(/<feMergeNode[^>]*>/g, '')
                   .replace(/<feFlood[^>]*>/g, '')
                   .replace(/<feTile[^>]*>/g, '')
                   .replace(/<feMorphology[^>]*>/g, '')
                   .replace(/<feImage[^>]*>/g, '')
                   .replace(/<feConvolveMatrix[^>]*>/g, '')
                   .replace(/<feDisplacementMap[^>]*>/g, '')
                   .replace(/<feTurbulence[^>]*>/g, '')
                   .replace(/<feDiffuseLighting[^>]*>.*?<\/feDiffuseLighting>/gs, '')
                   .replace(/<feSpecularLighting[^>]*>.*?<\/feSpecularLighting>/gs, '')
                   .replace(/<feDistantLight[^>]*>/g, '')
                   .replace(/<fePointLight[^>]*>/g, '')
                   .replace(/<feSpotLight[^>]*>/g, '')
                   .replace(/<feComponentTransfer[^>]*>.*?<\/feComponentTransfer>/gs, '')
                   .replace(/<feFuncR[^>]*>/g, '')
                   .replace(/<feFuncG[^>]*>/g, '')
                   .replace(/<feFuncB[^>]*>/g, '')
                   .replace(/<feFuncA[^>]*>/g, '')
                   .replace(/<feDropShadow[^>]*>/g, '')
                   .replace(/<feDropShadow[^>]*>.*?<\/feDropShadow>/gs, '');
      };
      
      const sanitizedHTML = sanitizeHTML(html);
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = sanitizedHTML;
      
      // Try multiple selectors for product cards
      let productCards = tempContainer.querySelectorAll('.grid__item');
      if (productCards.length === 0) {
        productCards = tempContainer.querySelectorAll('.card-wrapper, .card, [data-product-id]');
      }
      
      return Array.from(productCards);
    } catch (error) {
      console.error('Error extracting product cards from HTML:', error);
      return [];
    }
  }

  async tryFallbackRecommendations(productId) {
    
    try {
      // Try to fetch from a general collection to get more variety
      const collectionUrl = `https://youthlabdirect.com.au/collections/shop-all?section_id=main-collection-product-grid&page=1&view=ajax&limit=20`;
      
      const response = await fetch(collectionUrl);
      
      if (!response.ok) {
        throw new Error(`Collection fetch failed: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Extract product cards from the collection HTML
      const productCards = this.extractProductCardsFromHTML(html);
      
      if (productCards.length === 0) {
        throw new Error('No product cards found in collection');
      }
      
      // Process and filter the collection products
      const filteredProducts = await this.processCollectionProducts(productCards);
      
      if (filteredProducts.length === 0) {
        throw new Error('No products passed filtering');
      }
      
      // If we still don't have enough, try to get more from different pages
      if (filteredProducts.length < 6) {
        
        for (let page = 2; page <= 3 && filteredProducts.length < 6; page++) {
          try {
            const additionalUrl = `https://youthlabdirect.com.au/collections/shop-all?section_id=main-collection-product-grid&page=${page}&view=ajax&limit=20`;
            const additionalResponse = await fetch(additionalUrl);
            
            if (additionalResponse.ok) {
              const additionalHtml = await additionalResponse.text();
              const additionalCards = this.extractProductCardsFromHTML(additionalHtml);
              const additionalProducts = await this.processCollectionProducts(additionalCards);
              
              // Add unique products
              additionalProducts.forEach(product => {
                const productId = this.extractProductId(product);
                if (productId && !filteredProducts.some(p => this.extractProductId(p) === productId)) {
                  filteredProducts.push(product);
                }
              });
              
            }
          } catch (error) {
            break;
          }
        }
      }
      
      return filteredProducts;
      
    } catch (error) {
      console.error('Error in fallback recommendations:', error);
      return [];
    }
  }

  createTestRecommendations() {
    
    // Create a simple test recommendation to verify the system works
    const testHTML = `
      <div class="cart-recommendations__slider swiper">
        <div class="swiper-wrapper">
          <div class="swiper-slide">
            <div class="card-wrapper product-card-wrapper">
              <div class="card card--standard card--media">
                <div class="card__inner">
                  <div class="media media--transparent">
                    <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIgc3Ryb2tlPSIjZGRkIiBzdHJva2Utd2lkdGg9IjEiLz48dGV4dCB4PSIxMDAiIHk9IjEwMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+VGVzdCBQcm9kdWN0PC90ZXh0Pjwvc3ZnPg==" alt="Test Product" width="200" height="200">
                  </div>
                </div>
                <div class="card__content">
                  <h3 class="card__heading">Test Product</h3>
                  <div class="card__price">$19.99</div>
                  <button class="button button--small">Add to Cart</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    this.innerHTML = testHTML;
  }

  processCollectionProducts(productCards) {
    
    // Get cart product IDs to exclude
    const cartProductIds = this.dataset.cartProductIds?.split(',').filter(id => id.trim()) || [];
    
    // TEMPORARY: Disable availability filtering for testing
    const DISABLE_AVAILABILITY_FILTER = true;
    if (DISABLE_AVAILABILITY_FILTER) {
    }
    
    // Filter and process products
    const filteredProducts = [];
    
    for (let i = 0; i < productCards.length; i++) {
      const card = productCards[i];
      
      // Skip if it's the same product
      const cardProductId = card.querySelector('[data-product-id]')?.dataset.productId;
      
      if (cardProductId && cardProductId === this.dataset.productId) {
        continue;
      }
      
      // Skip if product is already in cart
      if (cardProductId && cartProductIds.includes(cardProductId)) {
        continue;
      }

      if (cardProductId && this.productIdInExclusiveCollection(cardProductId)) {
        continue;
      }
      
      // Skip if product type contains "Membership"
      const cardWrapper = card.querySelector('.card-wrapper') || card.closest('.card-wrapper') || card;
      const productType = cardWrapper.dataset.productType || 
                         cardWrapper.getAttribute('data-product-type');
      
      if (productType && productType.toLowerCase().includes('membership')) {
        continue;
      }
      
      // Skip if product is unavailable - but be less strict
      if (!DISABLE_AVAILABILITY_FILTER) {
        const badge = card.querySelector('.card__badge, .badge--sold-out, [data-available="false"]');
        if (badge) {
          
          // Only skip if it's clearly a sold-out badge
          const badgeText = badge.textContent.trim().toLowerCase();
          if (badgeText.includes('sold out') || 
              badgeText.includes('out of stock') || 
              badgeText.includes('unavailable') ||
              badge.classList.contains('badge--sold-out')) {
            continue;
          }
        }
      } else {
      }
      
      // Check if product has basic required elements
      const hasImage = card.querySelector('img');
      const titleElement = card.querySelector('.card__heading, .card-information__text, h3, h4');
      const hasTitle = titleElement && titleElement.textContent.trim();
      
      if (!hasImage || !hasTitle) {
        continue;
      }
      
      filteredProducts.push(card);
    }

    // Limit to maximum number of recommendations
    const maxRecommendations = parseInt(this.dataset.limit) || 6;
    const limitedProducts = filteredProducts.slice(0, maxRecommendations);
    
    if (limitedProducts.length === 0) {
      this.showNoRecommendations();
      return;
    }
    
    this.createSliderWithProducts(limitedProducts);
  }

  createSliderWithProducts(products) {
    
    // Sanitize HTML to prevent SVG parsing errors
    const sanitizeHTML = (html) => {
      // Remove problematic SVG elements that might cause parsing errors
      return html.replace(/<svg[^>]*>.*?<\/svg>/gs, '')
                 .replace(/<path[^>]*>/g, '')
                 .replace(/<g[^>]*>.*?<\/g>/gs, '')
                 .replace(/<defs[^>]*>.*?<\/defs>/gs, '')
                 .replace(/<clipPath[^>]*>.*?<\/clipPath>/gs, '')
                 .replace(/<use[^>]*>/g, '')
                 .replace(/<symbol[^>]*>.*?<\/symbol>/gs, '')
                 .replace(/<filter[^>]*>.*?<\/filter>/gs, '')
                 .replace(/<feGaussianBlur[^>]*>/g, '')
                 .replace(/<feOffset[^>]*>/g, '')
                 .replace(/<feComposite[^>]*>/g, '')
                 .replace(/<feBlend[^>]*>/g, '')
                 .replace(/<feColorMatrix[^>]*>/g, '')
                 .replace(/<feMerge[^>]*>.*?<\/feMerge>/gs, '')
                 .replace(/<feMergeNode[^>]*>/g, '')
                 .replace(/<feFlood[^>]*>/g, '')
                 .replace(/<feTile[^>]*>/g, '')
                 .replace(/<feMorphology[^>]*>/g, '')
                 .replace(/<feImage[^>]*>/g, '')
                 .replace(/<feConvolveMatrix[^>]*>/g, '')
                 .replace(/<feDisplacementMap[^>]*>/g, '')
                 .replace(/<feTurbulence[^>]*>/g, '')
                 .replace(/<feDiffuseLighting[^>]*>.*?<\/feDiffuseLighting>/gs, '')
                 .replace(/<feSpecularLighting[^>]*>.*?<\/feSpecularLighting>/gs, '')
                 .replace(/<feDistantLight[^>]*>/g, '')
                 .replace(/<fePointLight[^>]*>/g, '')
                 .replace(/<feSpotLight[^>]*>/g, '')
                 .replace(/<feComponentTransfer[^>]*>.*?<\/feComponentTransfer>/gs, '')
                 .replace(/<feFuncR[^>]*>/g, '')
                 .replace(/<feFuncG[^>]*>/g, '')
                 .replace(/<feFuncB[^>]*>/g, '')
                 .replace(/<feFuncA[^>]*>/g, '')
                 .replace(/<feDropShadow[^>]*>/g, '')
                 .replace(/<feDropShadow[^>]*>.*?<\/feDropShadow>/gs, '');
    };
    
    // Create clean product cards for the slider
    const createProductCard = (card) => {
      const productId = this.extractProductId(card);
      const title = card.querySelector('.card__heading, .card-information__text, h3, h4')?.textContent?.trim() || 'Product';
      const image = card.querySelector('img')?.src || '';
      
      // Extract vendor information from multiple sources
      let vendor = '';
      
      // Primary: Try standard Shopify vendor fields first
      vendor = card.querySelector('.card__vendor, .card-information__vendor, .vendor, .product-vendor, .product-brand')?.textContent?.trim() || '';
      
      // Secondary: Try data attributes
      if (!vendor) {
        vendor = card.querySelector('[data-vendor]')?.dataset.vendor || 
                card.querySelector('[data-product-vendor]')?.dataset.productVendor || '';
      }
      
      // Tertiary: Try meta tags
      if (!vendor) {
        const vendorMeta = card.querySelector('meta[property="product:brand"], meta[name="vendor"]');
        if (vendorMeta) {
          vendor = vendorMeta.getAttribute('content') || '';
        }
      }
      
      // Fallback: Try to extract vendor from URL path
      if (!vendor) {
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink) {
          const url = productLink.href;
          // Look for vendor in URL patterns like /collections/vendor-name/products/product-name
          const urlMatch = url.match(/\/collections\/([^\/]+)\/products\//);
          if (urlMatch && urlMatch[1]) {
            vendor = urlMatch[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        }
      }
      
      // Last resort: Check old bold-product-json (deprecated app data)
      if (!vendor) {
        const boldProductJson = card.querySelector('.bold-product-json');
        if (boldProductJson) {
          try {
            const productData = JSON.parse(boldProductJson.textContent);
            vendor = productData.vendor || productData.brand || '';
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }
      
      // Extract clean price text
      const price = this.extractCleanPrice(card);
      
      // Extract compare at price
      const compareAtPrice = this.extractCompareAtPrice(card);
      
      const url = card.querySelector('a')?.href || '#';
      
      // Try to find variant ID - look for variant selector or form
      let variantId = null;
      
      // Look for variant selector
      const variantSelector = card.querySelector('select[name="id"], input[name="id"][type="hidden"], .variant-selector');
      if (variantSelector) {
        variantId = variantSelector.value || variantSelector.dataset.variantId;
      }
      
      // If no variant selector, try to find it in the product form
      if (!variantId) {
        const productForm = card.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          const formVariantInput = productForm.querySelector('input[name="id"], select[name="id"]');
          if (formVariantInput) {
            variantId = formVariantInput.value;
          }
        }
      }
      
      // If still no variant ID, use the product ID as fallback (will need manual variant selection)
      if (!variantId) {
        variantId = productId;
      }
      
      // Determine availability
      const available = !card.querySelector('.card__badge, .badge--sold-out, [data-available="false"]');
      
      const productHTML = `
        <div class="swiper-slide">
          <div class="cart-recommendations__product">
            <div class="cart-recommendations__product-image">
              <img src="${image}" alt="${title}" width="200" height="200" loading="lazy">
            </div>
            <div class="cart-recommendations__product-info">
              ${vendor ? `<div class="cart-recommendations__product-vendor">${vendor}</div>` : ''}
              <h4 class="cart-recommendations__product-title">${title}</h4>
              <div class="cart-recommendations__product-price">
                <div class="cart-recommendations__price-row">
                  <span class="cart-recommendations__current-price">${price}</span>
                  ${compareAtPrice ? `<span class="cart-recommendations__compare-price">${compareAtPrice}</span>` : ''}
                </div>
              </div>
              <button class="cart-recommendations__add-to-cart" 
                      data-product-id="${productId}" 
                      data-variant-id="${variantId}"
                      onclick="addToCartFromRecommendations('${variantId}', this)">
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      `;
      
      return productHTML;
    };
    
    // Deduplicate products based on actual product data, not DOM elements
    const deduplicatedProducts = [];
    const seenProductIds = new Set();
    
    products.forEach((product, index) => {
      const productId = this.extractProductId(product);
      
      if (productId && !seenProductIds.has(productId)) {
        seenProductIds.add(productId);
        deduplicatedProducts.push(product);
      } else if (productId) {
      } else {
      }
    });
    
    // Use deduplicated products for the slider
    const maxRecommendations = parseInt(this.dataset.limit) || 6;
    
    const finalProducts = deduplicatedProducts.slice(0, maxRecommendations);
    
    // Create slider HTML
    const sliderHTML = `
      <div class="cart-recommendations__slider swiper">
        <div class="swiper-wrapper">
          ${finalProducts.map(card => createProductCard(card)).join('')}
        </div>
        ${finalProducts.length > 1 ? `
          <div class="swiper-pagination cart-recommendations__dots"></div>
          <div class="swiper-button-prev cart-recommendations__nav cart-recommendations__nav--prev" data-slider-prev></div>
          <div class="swiper-button-next cart-recommendations__nav cart-recommendations__nav--next" data-slider-next></div>
        ` : ''}
      </div>
    `;
    
    this.innerHTML = sliderHTML;
    
    // Initialize Swiper if available
    try {
      if (typeof Swiper !== 'undefined') {
        this.initializeSwiper();
      } else {
        this.initializeBasicSlider();
      }
    } catch (error) {
      console.error('Error initializing slider:', error);
      // Fallback to basic display without slider
      this.innerHTML = `
        <div class="cart-recommendations__basic">
          ${products.map(card => createProductCard(card)).join('')}
        </div>
      `;
    }
  }

  extractCleanPrice(card) {
    try {
      // Try to find the main price element
      const priceElement = card.querySelector('.price, .price__container, .card__price');
      
      if (!priceElement) {
        return 'Price not available';
      }
      
      // Look for specific price text elements
      const priceText = priceElement.querySelector('.price__regular, .price__sale, .price-item--regular, .price-item--sale');
      
      if (priceText) {
        // Get just the price text, clean it up
        let cleanPrice = priceText.textContent.trim();
        
        // Remove any CSS artifacts or extra text
        cleanPrice = cleanPrice.replace(/[^\d.,$]/g, ''); // Keep only numbers, dots, commas, and dollar signs
        
        // If we have a clean price, return it
        if (cleanPrice && cleanPrice.length > 0) {
          // Add $ if missing
          if (!cleanPrice.includes('$')) {
            cleanPrice = '$' + cleanPrice;
          }
          return cleanPrice;
        }
      }
      
      // Fallback: try to extract any price-like text from the price element
      let fallbackPrice = priceElement.textContent.trim();
      
      // Remove CSS and HTML artifacts more aggressively
      fallbackPrice = fallbackPrice
        .replace(/[^\d.,$]/g, '') // Keep only numbers, dots, commas, and dollar signs
        .replace(/\s+/g, '') // Remove all whitespace
        .replace(/^,+|,+$/g, '') // Remove leading/trailing commas
        .replace(/\.{2,}/g, '.') // Replace multiple dots with single dot
        .replace(/,$/, '') // Remove trailing comma
        .trim();
      
      // Clean up the price
      if (fallbackPrice && fallbackPrice.length > 0) {
        // Add $ if missing
        if (!fallbackPrice.includes('$')) {
          fallbackPrice = '$' + fallbackPrice;
        }
        return fallbackPrice;
      }
      
      // Last resort: try to find any text that looks like a price
      const allText = priceElement.textContent;
      const priceMatch = allText.match(/\$\s*\d+\.?\d*/);
      if (priceMatch) {
        const lastPrice = priceMatch[priceMatch.length - 1]; // Get the last price found
        return lastPrice;
      }
      
      return 'Price not available';
      
    } catch (error) {
      console.error('Error extracting price:', error);
      return 'Price not available';
    }
  }

  extractCompareAtPrice(card) {
    try {
      // Look for compare at price elements
      const compareAtElement = card.querySelector('.price__compare, .price-item--compare, .price__sale, .price-item--sale, [data-compare-price]');
      
      if (compareAtElement) {
        let compareAtPrice = compareAtElement.textContent.trim();
        
        // Clean up the price text
        compareAtPrice = compareAtPrice.replace(/[^\d.,$]/g, ''); // Keep only numbers, dots, commas, and dollar signs
        compareAtPrice = compareAtPrice.replace(/\s+/g, ''); // Remove all whitespace
        compareAtPrice = compareAtPrice.replace(/^,+|,+$/g, ''); // Remove leading/trailing commas
        compareAtPrice = compareAtPrice.replace(/\.{2,}/g, '.'); // Replace multiple dots with single dot
        compareAtPrice = compareAtPrice.replace(/,$/, ''); // Remove trailing comma
        compareAtPrice = compareAtPrice.trim();
        
        if (compareAtPrice && compareAtPrice.length > 0) {
          // Add $ if missing
          if (!compareAtPrice.includes('$')) {
            compareAtPrice = '$' + compareAtPrice;
          }
          return compareAtPrice;
        }
      }
      
      // Fallback: try to find any price-like text that might be a compare price
      const allPriceElements = card.querySelectorAll('.price, .price__container, .card__price, .price-item');
      for (const priceEl of allPriceElements) {
        const priceText = priceEl.textContent.trim();
        if (priceText.includes('Compare at') || priceText.includes('Was') || priceText.includes('Regular price')) {
          let compareAtPrice = priceText.replace(/[^\d.,$]/g, '');
          compareAtPrice = compareAtPrice.replace(/\s+/g, '');
          compareAtPrice = compareAtPrice.replace(/^,+|,+$/g, '');
          compareAtPrice = compareAtPrice.replace(/\.{2,}/g, '.');
          compareAtPrice = compareAtPrice.replace(/,$/, '');
          compareAtPrice = compareAtPrice.trim();
          
          if (compareAtPrice && compareAtPrice.length > 0) {
            if (!compareAtPrice.includes('$')) {
              compareAtPrice = '$' + compareAtPrice;
            }
            return compareAtPrice;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting compare at price:', error);
      return null;
    }
  }

  productIdInExclusiveCollection(productId) {
    const raw = this.dataset.exclusiveProductIds || '';
    if (!raw.trim() || productId == null || productId === '') return false;
    const idStr = String(productId).trim();
    return raw
      .split(',')
      .some((id) => id.trim() !== '' && id.trim() === idStr);
  }

  extractProductId(card) {
    
    // Try multiple selectors to find the product ID
    const dataProductId = card.querySelector('[data-product-id]')?.dataset.productId;
    const attrProductId = card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
    const cardProductId = card.querySelector('.card')?.dataset.productId;
    const cardAttrProductId = card.querySelector('.card')?.getAttribute('data-product-id');
    
    const productId = dataProductId || attrProductId || cardProductId || cardAttrProductId;
    
    return productId;
  }

  filterRecommendations(html) {
    
    // Create a temporary container to parse the HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = html;
    
    // Look for the product grid specifically, not the entire section
    const productGrid = tempContainer.querySelector('.product-grid, .grid, ul[role="list"]');
    
    if (!productGrid) {
      return null;
    }
    
    // Extract only the top-level product cards, not nested elements
    const productCards = productGrid.querySelectorAll('.grid__item');
    
    // If no grid__item elements found, fall back to other selectors
    if (productCards.length === 0) {
      const fallbackCards = productGrid.querySelectorAll('.card-wrapper, .card, [data-product-id]');
      return fallbackCards;
    }
    
    if (productCards.length === 0) {
      return null;
    }
    
    // Filter out products already in cart
    const cartProductIds = this.dataset.cartProductIds ? 
      this.dataset.cartProductIds.split(',').filter(id => id.trim()) : [];
    
    const filteredCards = Array.from(productCards).filter(card => {
      const productId = this.extractProductId(card);
      if (!productId) return false;
      
      if (cartProductIds.includes(productId.toString())) {
        return false;
      }
      
      // Filter out products with product type containing "Membership"
      const cardWrapper = card.querySelector('.card-wrapper') || card.closest('.card-wrapper') || card;
      const productType = cardWrapper.dataset.productType || 
                         cardWrapper.getAttribute('data-product-type');
      
      if (productType && productType.toLowerCase().includes('membership')) {
        return false;
      }

      if (this.productIdInExclusiveCollection(productId)) {
        return false;
      }
      
      return true;
    });
    
    // Limit to maximum number of recommendations (default 6)
    const maxRecommendations = parseInt(this.dataset.limit) || 6;
    
    const limitedCards = filteredCards.slice(0, maxRecommendations);
    
    if (filteredCards.length > maxRecommendations) {
    }
    
    return limitedCards;
  }

  initializeSlider() {
    // Wait for the cart drawer to be fully visible
    this.waitForCartDrawer();
  }

  waitForCartDrawer() {
    const cartDrawer = this.closest('.drawer, .cart-drawer, [data-cart-drawer]');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      // Cart drawer is active, wait a bit more for full rendering
      setTimeout(() => {
        this.createSlider();
      }, 500);
    } else {
      // Wait for cart drawer to become active
      setTimeout(() => {
        this.waitForCartDrawer();
      }, 100);
    }
  }

  createSlider() {
    const productCards = this.querySelectorAll('.grid__item');
    
    if (productCards.length === 0) {
      this.showNoRecommendations();
      return;
    }

    // Get the limit from data attribute or default to 6
    const maxRecommendations = parseInt(this.dataset.limit) || 6;
    const limitedCards = Array.from(productCards).slice(0, maxRecommendations);
    
    // Create slider HTML
    const sliderHTML = `
      <div class="cart-recommendations__slider swiper">
        <div class="swiper-wrapper">
          ${limitedCards.map(card => `
            <div class="swiper-slide">
              ${card.outerHTML}
            </div>
          `).join('')}
        </div>
        ${limitedCards.length > 1 ? `
          <div class="swiper-pagination cart-recommendations__dots"></div>
          <div class="swiper-button-prev cart-recommendations__nav cart-recommendations__nav--prev" data-slider-prev></div>
          <div class="swiper-button-next cart-recommendations__nav cart-recommendations__nav--next" data-slider-next></div>
        ` : ''}
      </div>
    `;
    
    this.innerHTML = sliderHTML;
    
    // Initialize Swiper if available
    if (typeof Swiper !== 'undefined') {
      this.initializeSwiper();
    } else {
      this.initializeBasicSlider();
    }
  }

  initializeSwiper() {
    // Prevent multiple initializations
    if (this.swiperInitialized) {
      return;
    }

    // Add retry limit to prevent infinite loops
    if (!this.retryCount) {
      this.retryCount = 0;
    }
    
    if (this.retryCount >= 5) {
      console.error('Swiper initialization failed after 5 attempts, giving up');
      this.swiperInitialized = true;
      return;
    }

    try {
      // Wait a bit for the DOM to be ready
      setTimeout(() => {
        const sliderElement = this.querySelector('.cart-recommendations__slider');
        if (!sliderElement) {
          console.error('Slider element not found');
          this.swiperInitialized = true;
          return;
        }
        
        // Check if element has dimensions
        const rect = sliderElement.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(sliderElement);
        
        // Check if slides are properly created
        const slides = sliderElement.querySelectorAll('.swiper-slide');
        
        if (sliderElement.offsetWidth === 0 || sliderElement.offsetHeight === 0) {
          // Increment retry count and try again
          this.retryCount++;
          setTimeout(() => {
            if (!this.swiperInitialized) {
              this.initializeSwiper();
            }
          }, 200); // Increased timeout
          return;
        }
        
        // Initialize Swiper with proper settings
        const swiper = new Swiper(sliderElement, {
          slidesPerView: 1,
          spaceBetween: 20,
          loop: false,
          width: null, // Let Swiper calculate based on container
          autoHeight: false,
          watchOverflow: true,
          preventClicks: false,
          preventClicksPropagation: false,
          setWrapperSize: true,
          // Try setting a specific slide width
          slidesPerGroup: 1,
          slidesPerGroupSkip: 0,
          slidesPerGroupAuto: false,
          navigation: {
            nextEl: sliderElement.querySelector('.swiper-button-next'),
            prevEl: sliderElement.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: sliderElement.querySelector('.swiper-pagination'),
            clickable: true,
          },
          breakpoints: {
            640: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
          },
          resizeObserver: false,
          observer: true,
          observeParents: true,
          on: {
            init: function() {
              // Override any inline styles that Swiper might have added
              const slides = this.slides;
              slides.forEach(slide => {
                slide.style.width = '100%';
                slide.style.maxWidth = '100%';
                slide.style.minWidth = '0';
              });
              
              // Force Swiper to recalculate with our constraints
              setTimeout(() => {
                this.updateSize();
                this.updateSlides();
                this.updateProgress();
                this.updateSlidesClasses();
                
                // Force slide width again
                this.slides.forEach(slide => {
                  slide.style.width = '100%';
                  slide.style.maxWidth = '100%';
                  slide.style.minWidth = '0';
                });
              }, 100);
            }
          }
        });
        
        // Mark as initialized to prevent loops
        this.swiperInitialized = true;
        
      }, 100);
      
    } catch (error) {
      console.error('Error initializing Swiper:', error);
      this.swiperInitialized = true; // Mark as attempted to prevent loops
    }
  }

  initializeBasicSlider() {
    const slides = this.querySelectorAll('.swiper-slide');
    const dots = this.querySelectorAll('.swiper-pagination .swiper-pagination-bullet');
    const prevBtn = this.querySelector('.swiper-button-prev');
    const nextBtn = this.querySelector('.swiper-button-next');
    
    if (slides.length <= 1) {
      this.hideNavigation();
      return;
    }
    
    let currentSlide = 0;
    
    const showSlide = (index) => {
      slides.forEach((slide, i) => {
        slide.style.display = i === index ? 'block' : 'none';
      });
      
      dots.forEach((dot, i) => {
        dot.classList.toggle('swiper-pagination-bullet-active', i === index);
      });
    };
    
    const nextSlide = () => {
      currentSlide = (currentSlide + 1) % slides.length;
      showSlide(currentSlide);
    };
    
    const prevSlide = () => {
      currentSlide = (currentSlide - 1 + slides.length) % slides.length;
      showSlide(currentSlide);
    };
    
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);
    if (prevBtn) prevBtn.addEventListener('click', prevSlide);
    
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        currentSlide = index;
        showSlide(currentSlide);
      });
    });
    
    showSlide(0);
  }

  hideNavigation() {
    const nav = this.querySelector('.swiper-pagination, .swiper-button-prev, .swiper-button-next');
    if (nav) nav.style.display = 'none';
  }

  showNoRecommendations() {
    
    // Show a simple message
    this.innerHTML = `
      <div class="cart-recommendations__no-results">
        <p>No recommendations available at the moment.</p>
        <button class="cart-recommendations__retry" onclick="this.closest('cart-product-recommendations').retryRecommendations()">
          Try Again
        </button>
      </div>
    `;
    
    // Also try to create test recommendations for debugging
    const productId = this.dataset.productId;
    if (productId) {
      this.createTestRecommendations();
    }
  }
  
  retryRecommendations() {
    const productId = this.dataset.productId;
    if (productId) {
      this.loadRecommendations(productId);
    }
  }

  showSuccessNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-recommendations__notification cart-recommendations__notification--success';
    notification.innerHTML = `
      <div class="cart-recommendations__notification-content">
        <span class="cart-recommendations__notification-icon">✓</span>
        <span class="cart-recommendations__notification-text">${message}</span>
      </div>
    `;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);
    
    // Remove notification after delay
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
  
  showErrorNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-recommendations__notification cart-recommendations__notification--error';
    notification.innerHTML = `
      <div class="cart-recommendations__notification-content">
        <span class="cart-recommendations__notification-icon">✕</span>
        <span class="cart-recommendations__notification-text">${message}</span>
      </div>
    `;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);
    
    // Remove notification after delay
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  disconnectedCallback() {
    this.observer?.unobserve(this);
  }
}

// Global function for Add to Cart button
window.addToCartFromRecommendations = async function(variantId, buttonElement) {
  try {
    // Show loading state
    const button = buttonElement;
    if (!button) {
      console.error('Button element not found');
      return;
    }
    
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;
    
    // Validate variant ID
    const numericVariantId = parseInt(variantId);
    if (isNaN(numericVariantId)) {
      throw new Error(`Invalid variant ID: ${variantId}`);
    }
    
    // Add to cart using Shopify's AJAX API with sections
    // This is the same approach as the free gift widget
    const cartDrawer = document.querySelector('cart-drawer');
    
    const sectionsToRender = cartDrawer?.getSectionsToRender?.()?.map(section => section.section) || ['cart-drawer', 'cart-icon-bubble'];
    
    // Fallback: if sections are undefined, use the correct section IDs
    const finalSections = sectionsToRender.filter(Boolean).length > 0 ? sectionsToRender : ['cart-drawer', 'cart-icon-bubble'];
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: numericVariantId,
          quantity: 1
        }],
        sections: finalSections,
        sections_url: window.location.pathname
      })
    });
    
    if (!response.ok) {
      // Get more details about the error
      let errorDetails = '';
      try {
        const errorResponse = await response.text();
        errorDetails = errorResponse;
      } catch (e) {
        errorDetails = 'Could not read error response';
      }
      
      throw new Error(`HTTP error! status: ${response.status}, details: ${errorDetails}`);
    }
    
    const result = await response.json();
    
    const productTitle = button.closest('.cart-recommendations__product')?.querySelector('.cart-recommendations__product-title')?.textContent || 'Product';
    
    button.textContent = 'Added!';
    button.style.background = '#28a745';
    
    // Re-render cart drawer and cart icon with section HTML so the new item appears without refresh
    if (cartDrawer && result.sections && Object.keys(result.sections).length > 0 && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(result);
    }
    
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-recommendations',
        cartData: result
      });
    }
    
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { cartData: result, source: 'cart-recommendations' }
    }));
    document.dispatchEvent(new CustomEvent('cart:drawer:updated', { 
      detail: { cartData: result, source: 'cart-recommendations' }
    }));
    
    const cartRecommendationsElement = document.querySelector('cart-product-recommendations');
    if (cartRecommendationsElement) {
      cartRecommendationsElement.showSuccessNotification(`${productTitle} added to cart!`);
    }
    
    // Reset button after delay
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.style.background = '';
    }, 2000);
    
  } catch (error) {
    console.error('Error adding product to cart:', error);
    
    // Show error notification
    const cartRecommendationsElement = document.querySelector('cart-product-recommendations');
    if (cartRecommendationsElement) {
      cartRecommendationsElement.showErrorNotification('Failed to add product to cart');
    }
    
    // Show error state
    if (buttonElement) {
      buttonElement.textContent = 'Error!';
      buttonElement.style.background = '#dc3545';
      
      // Reset button after delay
      setTimeout(() => {
        buttonElement.textContent = 'Add to Cart';
        buttonElement.disabled = false;
        buttonElement.style.background = '';
      }, 2000);
    }
  }
};

// Define the custom element
customElements.define('cart-product-recommendations', CartProductRecommendations);


